import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useMealPlanningStore } from '@/stores/meal-planning-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { ingredientAggregator, type AggregatedShoppingItem, type MealPlanShoppingListData } from '@/services/ingredients'
import { ClipboardList, ShoppingCart, Wand2 } from 'lucide-react'
import { SavedShoppingList } from './saved-shopping-list'
import { ShoppingListPreview } from './shopping-list-preview'
import { ShoppingListDateRangePicker } from './shopping-list-date-range-picker'

interface DateRangeOption {
  label: string
  value: string
  start_date: string
  end_date: string
}

interface MealPlanShoppingListProps {
  className?: string
}

type SavedShoppingItem = {
  name: string
  quantity?: number
  unit?: string
  specification?: string
  recipe_titles: string[]
  is_purchased: boolean
  checked: boolean
  notes?: string
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
  })

const normalizeSavedShoppingItem = (item: unknown): SavedShoppingItem => {
  const raw = (item ?? {}) as Record<string, unknown>
  const quantity = typeof raw.quantity === 'number' ? raw.quantity : undefined

  return {
    name:
      (typeof raw.name === 'string' && raw.name) ||
      (typeof raw.ingredient === 'string' && raw.ingredient) ||
      'Onbekend item',
    quantity,
    unit: typeof raw.unit === 'string' ? raw.unit : undefined,
    specification: typeof raw.specification === 'string' ? raw.specification : undefined,
    recipe_titles: Array.isArray(raw.recipe_titles) ? raw.recipe_titles.filter((title): title is string => typeof title === 'string') : [],
    is_purchased: Boolean(raw.is_purchased),
    checked: Boolean(raw.checked),
    notes: typeof raw.notes === 'string' ? raw.notes : undefined,
  }
}

export function MealPlanShoppingList({ className }: MealPlanShoppingListProps) {
  const {
    currentMealPlan,
    plannedMeals,
    getWeekDates,
    loadShoppingList,
    generateShoppingList,
    currentShoppingList,
    loadingShoppingList,
    toggleShoppingItemChecked,
  } = useMealPlanningStore()
  const { recipes } = useRecipeStore()

  const [activeTab, setActiveTab] = useState<'generate' | 'existing'>('generate')
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption | null>(null)
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' })
  const [useCustomRange, setUseCustomRange] = useState(false)
  const [previewData, setPreviewData] = useState<MealPlanShoppingListData | null>(null)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  const filterMealTypes = ['diner']

  const dateRangeOptions = useMemo<DateRangeOption[]>(() => {
    const currentWeek = getWeekDates()
    const thisWeekStart = currentWeek.start.toISOString().split('T')[0]
    const thisWeekEnd = currentWeek.end.toISOString().split('T')[0]

    const nextWeekStart = new Date(currentWeek.start)
    nextWeekStart.setDate(nextWeekStart.getDate() + 7)
    const nextWeekEnd = new Date(nextWeekStart)
    nextWeekEnd.setDate(nextWeekEnd.getDate() + 6)

    const twoWeeksEnd = new Date(nextWeekEnd)
    twoWeeksEnd.setDate(twoWeeksEnd.getDate() + 7)

    return [
      {
        label: 'Deze week',
        value: 'this_week',
        start_date: thisWeekStart,
        end_date: thisWeekEnd,
      },
      {
        label: 'Volgende week',
        value: 'next_week',
        start_date: nextWeekStart.toISOString().split('T')[0],
        end_date: nextWeekEnd.toISOString().split('T')[0],
      },
      {
        label: 'Komende 2 weken',
        value: 'two_weeks',
        start_date: thisWeekStart,
        end_date: twoWeeksEnd.toISOString().split('T')[0],
      },
    ]
  }, [getWeekDates])

  useEffect(() => {
    if (!selectedDateRange && dateRangeOptions.length > 0) {
      setSelectedDateRange(dateRangeOptions[0])
    }
  }, [dateRangeOptions, selectedDateRange])

  useEffect(() => {
    if (currentMealPlan?.id) {
      void loadShoppingList(currentMealPlan.id)
    }
  }, [currentMealPlan?.id, loadShoppingList])

  const effectiveDateRange = useMemo(() => {
    if (useCustomRange && customDateRange.start && customDateRange.end) {
      return customDateRange
    }

    if (!selectedDateRange) {
      return null
    }

    return {
      start: selectedDateRange.start_date,
      end: selectedDateRange.end_date,
    }
  }, [customDateRange, selectedDateRange, useCustomRange])

  const filteredPlannedMeals = useMemo(() => {
    if (!effectiveDateRange) {
      return []
    }

    return plannedMeals.filter(
      (meal) =>
        meal.planned_date >= effectiveDateRange.start &&
        meal.planned_date <= effectiveDateRange.end &&
        filterMealTypes.includes(meal.meal_type)
    )
  }, [effectiveDateRange, plannedMeals])

  const savedItems = useMemo(
    () => (Array.isArray(currentShoppingList?.ingredients) ? currentShoppingList.ingredients.map(normalizeSavedShoppingItem) : []),
    [currentShoppingList]
  )

  useEffect(() => {
    if (!currentMealPlan || !effectiveDateRange) {
      setPreviewData(null)
      return
    }

    setIsGeneratingPreview(true)
    const shoppingData = ingredientAggregator.aggregateFromPlannedMeals(filteredPlannedMeals, recipes, {
      start_date: effectiveDateRange.start,
      end_date: effectiveDateRange.end,
    })
    setPreviewData(shoppingData)
    setIsGeneratingPreview(false)
  }, [currentMealPlan, effectiveDateRange, filteredPlannedMeals, recipes])

  const handleGenerateShoppingList = async () => {
    if (!currentMealPlan || !previewData) {
      return
    }

    setIsGenerating(true)
    const result = await generateShoppingList(
      currentMealPlan.id,
      previewData.date_range.start_date,
      previewData.date_range.end_date
    )
    setIsGenerating(false)

    if (result) {
      setActiveTab('existing')
      void loadShoppingList(currentMealPlan.id)
    }
  }

  const handlePreviewItemUpdate = (itemIndex: number, updates: Partial<AggregatedShoppingItem>) => {
    if (!previewData) {
      return
    }

    const updatedItems = [...previewData.all_items]
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], ...updates }

    setPreviewData({
      ...previewData,
      all_items: updatedItems,
      categories: ingredientAggregator.groupByCategory(updatedItems),
    })
  }

  const handlePreviewItemAdd = (item: Omit<AggregatedShoppingItem, 'recipe_ids' | 'recipe_titles' | 'category'>) => {
    if (!previewData) {
      return
    }

    const updatedItems = [
      ...previewData.all_items,
      {
        ...item,
        recipe_ids: [],
        recipe_titles: [],
        category: ingredientAggregator.categorizeIngredient(item.name),
      },
    ]

    setPreviewData({
      ...previewData,
      all_items: updatedItems,
      categories: ingredientAggregator.groupByCategory(updatedItems),
    })
  }

  const handlePreviewItemRemove = (itemIndex: number) => {
    if (!previewData) {
      return
    }

    const updatedItems = previewData.all_items.filter((_, index) => index !== itemIndex)
    setPreviewData({
      ...previewData,
      all_items: updatedItems,
      categories: ingredientAggregator.groupByCategory(updatedItems),
    })
  }

  const handleExport = async (format: 'text' | 'csv' | 'json') => {
    if (!previewData) {
      return
    }

    const exportData = ingredientAggregator.exportToFormat(previewData, format)
    const filename = `boodschappenlijst-${previewData.date_range.start_date}-${previewData.date_range.end_date}`

    if (format === 'text') {
      const copied = await navigator.clipboard.writeText(exportData).then(() => true).catch(() => false)
      setCopySuccess(copied)
      if (copied) {
        setTimeout(() => setCopySuccess(false), 2500)
      }
      return
    }

    const mimeType = format === 'csv' ? 'text/csv' : 'application/json'
    const blob = new Blob([exportData], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.${format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  if (!currentMealPlan) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center">
          <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Geen maaltijdplan geselecteerd</h3>
          <p className="text-gray-600">Selecteer eerst een maaltijdplan om een boodschappenlijst te maken.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Boodschappenlijst
            </CardTitle>
            <p className="text-sm text-gray-600">Maak een nieuwe lijst voor deze week of werk verder in je laatste opgeslagen versie.</p>
          </div>

          <Badge variant="outline">{currentMealPlan.name}</Badge>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Geplande diners</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{filteredPlannedMeals.length}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Preview-items</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{previewData?.all_items.length ?? 0}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Opgeslagen lijst</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {savedItems.length > 0
                ? `${savedItems.filter((item) => item.is_purchased || item.checked).length}/${savedItems.length}`
                : 'Nog leeg'}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="flex flex-wrap gap-2">
          <Button variant={activeTab === 'generate' ? 'primary' : 'outline'} onClick={() => setActiveTab('generate')}>
            <Wand2 className="mr-2 h-4 w-4" />
            Nieuwe lijst
          </Button>
          <Button variant={activeTab === 'existing' ? 'primary' : 'outline'} onClick={() => setActiveTab('existing')}>
            <ClipboardList className="mr-2 h-4 w-4" />
            Bestaande lijst
          </Button>
        </div>

        {activeTab === 'generate' ? (
          <div className="space-y-6">
            <ShoppingListDateRangePicker
              dateRangeOptions={dateRangeOptions}
              selectedDateRange={selectedDateRange}
              useCustomRange={useCustomRange}
              customDateRange={customDateRange}
              onSelectedDateRangeChange={setSelectedDateRange}
              onUseCustomRangeChange={setUseCustomRange}
              onCustomDateRangeChange={setCustomDateRange}
            />

            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline">{filteredPlannedMeals.length} diners</Badge>
              <Badge variant="outline">{previewData?.all_items.length ?? 0} boodschappen</Badge>
              {copySuccess && (
                <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
                  Tekst gekopieerd
                </Badge>
              )}
            </div>

            {isGeneratingPreview ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                <div className="mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-b-2 border-gray-500"></div>
                <p className="text-sm text-gray-600">Voorvertoning opbouwen...</p>
              </div>
            ) : previewData ? (
              <ShoppingListPreview
                shoppingData={previewData}
                onItemUpdate={handlePreviewItemUpdate}
                onItemAdd={handlePreviewItemAdd}
                onItemRemove={handlePreviewItemRemove}
                onGenerate={handleGenerateShoppingList}
                onExport={handleExport}
                isGenerating={isGenerating}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
                Selecteer een geldige periode om de boodschappenlijst te bekijken.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <SavedShoppingList
              loadingShoppingList={loadingShoppingList}
              currentMealPlanId={currentMealPlan.id}
              currentShoppingList={currentShoppingList}
              savedItems={savedItems}
              onRefresh={loadShoppingList}
              onToggleItemChecked={toggleShoppingItemChecked}
              onSwitchToGenerate={() => setActiveTab('generate')}
              formatDate={formatDate}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
